import { Component, EventEmitter, Input, OnInit } from "@angular/core";
import { RequestUtilsService } from "@core/services";
import { catchError, forkJoin, of, Subscription } from "rxjs";
import { UserService } from '../../../../core/services/user.service';

@Component({
  selector: "app-custom-request-details",
  templateUrl: "./custom-request-details.component.html",
  styleUrls: ["./custom-request-details.component.scss"],
})
export class CustomRequestDetailsComponent implements OnInit {
  @Input() hasPrivileges: boolean;
  @Input() request: any;
  @Input() user: any;
  icon: string;
  comments: string;
  sendInfo: EventEmitter<boolean> = new EventEmitter<boolean>();
  roomTravellersDisplay: { roomNumber: number; travellers: string }[] = [];
  flightTravellersDisplay: string[] = [];
  carTravellersDisplay: string[] = [];
  flightTravellersCount = 0;

  subscriptions: Subscription[] = [];

  constructor(
    private requestUtilsService: RequestUtilsService,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.request = JSON.parse(JSON.stringify(this.request));
    this.getIcon();

    if (this.request.type?.toLowerCase() === "hotel") {
      if (this.request.comments?.includes("+ (rooms)")) {
        this.request.rooms =
          this.request.comments.length > 0
            ? this.request.comments
                .split("+ (rooms):")[1]
                .split("+ (people):")[0]
                .trim()
            : "";

        this.request.people =
          this.request.comments.length > 0
            ? this.request.comments.split("+ (people):")[1].trim()
            : "";

        this.request.comments =
          this.request.comments.length > 0
            ? this.request.comments
                .split("(comments):")[1]
                .split("+ (rooms):")[0]
                .trim()
            : "";
      }

      this.buildRoomTravellersDisplay();
    }

    if (
      this.request.type?.toLowerCase() === "flight" ||
      this.request.type?.toLowerCase() === "train"
    ) {
      this.loadTravellersData();
    }

    if (this.request.type?.toLowerCase() === "car") {
      this.buildCarTravellersDisplay();
    }
  }

  getIcon() {
    switch (this.request.type?.toLowerCase()) {
      case "hotel":
        this.icon = "assets/img/svg/building.svg";
        break;
      case "flight":
        this.icon = "assets/img/svg/flight-black.svg";
        break;
      case "car":
        this.icon = "assets/img/svg/car-black.svg";
        break;
      case "train":
        this.icon = "assets/img/svg/sidebar/train.svg";
        break;
      case "airbnb":
        this.icon = "assets/img/svg/airbnb.svg";
        break;
      default:
        break;
    }
  }

  buildRoomTravellersDisplay(): void {
    const rooms = Array.isArray(this.request?.travelersPerRoom)
      ? this.request.travelersPerRoom
      : [];

    this.roomTravellersDisplay = rooms
      .map((room: any, index: number) => {
        const travellers = Array.isArray(room?.travelers) ? room.travelers : [];

        const travellerNames = travellers
          .map((traveller: any) =>
            `${traveller?.name || ""} ${traveller?.lastname || ""}`.trim()
          )
          .filter((fullName: string) => !!fullName)
          .join(", ");

        return {
          roomNumber: room?.roomNumber || index + 1,
          travellers: travellerNames,
        };
      })
      .filter((room: any) => !!room.travellers);
  }

  loadTravellersData(): void {
    const uuids = Array.isArray(this.request?.travellers)
      ? this.request.travellers
      : this.request?.userUuid
      ? [this.request.userUuid]
      : [];

    const uniqueUuids = [...new Set(uuids.filter((uuid: string) => !!uuid))];
    this.flightTravellersCount = uniqueUuids.length;

    if (!uniqueUuids.length) {
      this.flightTravellersDisplay = [];
      return;
    }

    const requests = uniqueUuids.map((uuid: string) =>
      this.userService.getUserByUuid(uuid).pipe(
        catchError(() => of(null))
      )
    );

    const sub = forkJoin(requests).subscribe((users: any[]) => {
      this.flightTravellersDisplay = users
        .filter((user: any) => !!user)
        .map((user: any) => `${user?.name || ""} ${user?.lastname || ""}`.trim())
        .filter((fullName: string) => !!fullName);
    });

    this.subscriptions.push(sub);
  }

  buildCarTravellersDisplay(): void {
    const travellers = Array.isArray(this.request?.traveller)
      ? this.request.traveller
      : [];

    this.carTravellersDisplay = travellers
      .map((traveller: any) => {
        const name = traveller?.name || "";
        const lastname = traveller?.lastname || traveller?.surname || "";
        return `${name} ${lastname}`.trim();
      })
      .filter((fullName: string) => !!fullName);
  }

  addBook() {
    switch (this.request.type?.toLowerCase()) {
      case "hotel":
        this.requestUtilsService.createCustomHotel(
          this.request,
          this.hasPrivileges
        );
        break;
      case "flight":
        this.requestUtilsService.createCustomFlightTrain(
          this.request,
          "flight",
          this.hasPrivileges
        );
        break;
      case "train":
        this.requestUtilsService.createCustomFlightTrain(
          this.request,
          "train",
          this.hasPrivileges
        );
        break;
      case "car":
        if (this.request.price) {
          this.request.amount = this.request.price;
          delete this.request.price;
        }
        this.requestUtilsService.createCustomCar(
          this.request,
          this.hasPrivileges
        );
        break;
      case "airbnb":
        this.requestUtilsService.createCustomAirbnb(
          this.request,
          this.hasPrivileges
        );
        break;
      default:
        break;
    }
  }

  isValidDate(date: any): boolean {
    return date && !isNaN(Date.parse(date));
  }

  isValidUrl(value: string): boolean {
    if (!value || value.toLowerCase() === "airbnb") {
      return false;
    }
    const urlPattern = /^(https?:\/\/)?([\w\-]+\.)+[\w]{2,}(\/\S*)?$/;
    return urlPattern.test(value);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
}